import { useState, useEffect } from "react";
import { CheckCircle2, XCircle, FileText, ExternalLink, Sparkles } from "lucide-react";
import { api } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { AsyncButton } from "./ui/async-button.js";

export interface LightboxTarget {
  versionId: string;
  docType: string;
  version: number;
  mimeType: string | null;
  extractedData: Record<string, unknown> | null;
  currentStatus: "UNVERIFIED" | "VERIFIED" | "REJECTED";
  isHR: boolean;
  /** View-only profile: preview only, no verify/reject or re-extract actions. */
  readOnly?: boolean | undefined;
  /** HRMS-0634: open the lightbox already in reject mode so HR confirms-and-sends
   *  in two clicks instead of hunting through the version history. Honoured only
   *  when the latest version is UNVERIFIED and HR has edit access. */
  startInRejectMode?: boolean | undefined;
  /** HRMS-0634: initial text for the rejection reason textarea. HR can still edit
   *  it before pressing Confirm Reject. */
  initialRejectReason?: string | undefined;
}

interface Props {
  target: LightboxTarget;
  onClose: () => void;
  onDone: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  panNumber: "PAN Number", aadhaarNumber: "Aadhaar Number", name: "Name",
  dateOfBirth: "Date of Birth", gender: "Gender", fatherName: "Father's Name",
  accountNumber: "Account Number", ifscCode: "IFSC Code", bankName: "Bank Name",
  branchName: "Branch", accountHolderName: "Account Holder",
};

const AI_EXTRACTABLE_TYPES = new Set(["AADHAAR", "PAN", "BANK_DOCUMENT", "RESUME"]);

export function DocumentLightbox({ target, onClose, onDone }: Props) {
  const [notes, setNotes]           = useState("");
  // HRMS-0634: pre-switch into reject mode with a reason pre-filled when the
  // target asks for it. Only honoured for the still-unverified latest version
  // on HR's own view — a verified/rejected card doesn't render the reject UI
  // anyway (see the guard around line 177), so pre-switching is a no-op there.
  const [rejectReason, setRejectReason] = useState(target.initialRejectReason ?? "");
  const [rejectMode, setRejectMode] = useState(
    !!target.startInRejectMode && target.isHR && !target.readOnly && target.currentStatus === "UNVERIFIED",
  );
  const [busy, setBusy]             = useState(false);
  const [actionError, setActionError] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [extractedOverride, setExtractedOverride] = useState<Record<string, unknown> | null>(null);
  const [reExtracting, setReExtracting] = useState(false);
  const [reExtractError, setReExtractError] = useState("");

  useEffect(() => {
    api.get<{ url: string }>(`/documents/versions/${target.versionId}/url`)
      .then(({ data }) => setPreviewUrl(data.url))
      .catch((err) => logError("DocumentLightbox.loadPreviewUrl", err));
  }, [target.versionId]);

  async function reExtract() {
    setReExtracting(true); setReExtractError("");
    try {
      const { data } = await api.post<{ extractedData: Record<string, unknown> | null }>(`/documents/versions/${target.versionId}/extract`, {});
      setExtractedOverride(data.extractedData ?? {});
    } catch (err) {
      logError("DocumentLightbox.reExtract", err, { versionId: target.versionId });
      setReExtractError("Re-extraction failed. Please try again.");
      throw err;
    } finally {
      setReExtracting(false);
    }
  }

  async function verify() {
    setBusy(true); setActionError("");
    try {
      await api.patch(`/documents/versions/${target.versionId}/verify`, { notes: notes.trim() || undefined });
      onDone();
    } catch (err) {
      logError("DocumentLightbox.verify", err);
      setActionError("Could not verify this document. Please try again.");
      throw err;
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    if (!rejectReason.trim()) { setActionError("A rejection reason is required."); throw new Error("validation"); }
    setBusy(true); setActionError("");
    try {
      await api.patch(`/documents/versions/${target.versionId}/reject`, { reason: rejectReason.trim() });
      onDone();
    } catch (err) {
      logError("DocumentLightbox.reject", err);
      setActionError("Could not reject this document. Please try again.");
      throw err;
    } finally {
      setBusy(false);
    }
  }

  const extracted = extractedOverride ?? target.extractedData ?? {};
  const isImage = target.mimeType?.startsWith("image/");
  const canReExtract = !target.readOnly && AI_EXTRACTABLE_TYPES.has(target.docType) && Object.keys(extracted).length === 0;

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)",
          width: "min(860px, 95vw)", maxHeight: "90vh", overflow: "auto",
          padding: 28, display: "flex", flexDirection: "column", gap: 20,
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{target.docType} — v{target.version}</h3>
            {target.currentStatus === "VERIFIED" && <span style={{ fontSize: 11, color: "var(--color-success)", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}><CheckCircle2 size={12} /> Verified</span>}
            {target.currentStatus === "REJECTED" && <span style={{ fontSize: 11, color: "var(--color-error)", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}><XCircle size={12} /> Rejected</span>}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "var(--color-surface-400)", lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          {/* Document preview */}
          <div>
            <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, color: "var(--color-surface-500)", textTransform: "uppercase" }}>Document</p>
            {previewUrl && isImage ? (
              <img src={previewUrl} alt="Document" style={{ width: "100%", borderRadius: "var(--radius-md)", border: "1px solid var(--color-surface-200)" }} />
            ) : previewUrl ? (
              <a href={previewUrl} target="_blank" rel="noopener noreferrer" style={{
                display: "flex", alignItems: "center", justifyContent: "center", height: 160, gap: 8,
                border: "2px dashed var(--color-surface-300)", borderRadius: "var(--radius-md)",
                textDecoration: "none", color: "var(--color-brand-600)", fontWeight: 600, fontSize: 14,
              }}>
                <FileText size={16} /> Open PDF in new tab <ExternalLink size={14} />
              </a>
            ) : (
              <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-surface-400)", fontSize: 13, border: "1px solid var(--color-surface-100)", borderRadius: "var(--radius-md)" }}>
                Loading preview…
              </div>
            )}
          </div>

          {/* Extracted fields + actions */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, color: "var(--color-surface-500)", textTransform: "uppercase" }}>Extracted Data</p>
              {Object.keys(extracted).length > 0 ? Object.entries(extracted).map(([k, v]) => (
                <div key={k} style={{ display: "flex", gap: 8, fontSize: 13, marginBottom: 6 }}>
                  <span style={{ color: "var(--color-surface-500)", minWidth: 130 }}>{FIELD_LABELS[k] ?? k.replace(/([A-Z])/g, " $1")}</span>
                  <span style={{ fontWeight: 600, color: "var(--color-surface-900)" }}>{String(v ?? "—")}</span>
                </div>
              )) : (
                <p style={{ fontSize: 13, color: "var(--color-surface-400)" }}>No fields extracted.</p>
              )}
              {canReExtract && (
                <div style={{ marginTop: 8 }}>
                  <AsyncButton
                    onClick={() => reExtract()}
                    confirm={{
                      title: "Re-run AI extraction?",
                      description: "Reads this document again and replaces the extracted fields shown above with the new result.",
                      actionText: "Re-extract",
                      tone: "info",
                    }}
                    disabled={reExtracting}
                    variant="outline"
                    animated={false}
                    size="sm"
                  >
                    <Sparkles size={12} /> Re-extract
                  </AsyncButton>
                  {reExtractError && <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--color-error-fg)" }}>{reExtractError}</p>}
                </div>
              )}
            </div>

            {target.isHR && !target.readOnly && target.currentStatus === "UNVERIFIED" && (
              !rejectMode ? (
                <>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4, color: "var(--color-surface-700)" }}>Notes (optional)</label>
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Everything matches…"
                      style={{ width: "100%", padding: 8, border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-sm)", fontSize: 13, boxSizing: "border-box", resize: "vertical" }} />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => setRejectMode(true)} style={{ flex: 1, padding: "9px", border: "1px solid var(--color-error-border)", background: "var(--color-error-bg-subtle)", color: "var(--color-error-fg)", borderRadius: "var(--radius-md)", cursor: "pointer", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}><XCircle size={14} /> Reject</button>
                    <AsyncButton
                      disabled={busy}
                      onClick={() => verify()}
                      confirm={{
                        title: "Mark this document as verified?",
                        description: "Sets the document to VERIFIED, saves any notes you added, and ticks it off the employee's onboarding checklist.",
                        actionText: "Verify",
                        tone: "success",
                      }}
                      variant="success"
                      className="flex-1"
                    >
                      <CheckCircle2 size={14} /> Verify
                    </AsyncButton>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4, color: "var(--color-error-fg)" }}>Rejection Reason *</label>
                    <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} placeholder="Document is blurry / incorrect type…"
                      style={{ width: "100%", padding: 8, border: "1px solid var(--color-error-border)", borderRadius: "var(--radius-sm)", fontSize: 13, boxSizing: "border-box", resize: "vertical" }} />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => setRejectMode(false)} style={{ flex: 1, padding: "9px", border: "1px solid var(--color-surface-200)", background: "none", borderRadius: "var(--radius-md)", cursor: "pointer", fontSize: 13 }}>Back</button>
                    {/* No `confirm` prop: typing a reason and pressing this button is
                        already the confirmation step, so a card on top would just be noise. */}
                    <AsyncButton
                      disabled={busy || !rejectReason.trim()}
                      onClick={() => reject()}
                      variant="destructive"
                      className="flex-1"
                    >
                      Confirm Reject
                    </AsyncButton>
                  </div>
                </>
              )
            )}
            {actionError && <p style={{ margin: 0, fontSize: 12, color: "var(--color-error-fg)" }}>{actionError}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
